/**
 * ScheduleFollowupDialog — reusable modal for creating an AR follow-up task.
 * Pre-fills title with "Call about <customer>" and lets the operator pick a
 * due date, optional invoice, assignee (any user in the tenant), and notes.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { CalendarBlank, User, ListChecks } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { createArFollowup, listTenantUsers } from '../lib/api';

function defaultDueIso(daysAhead = 3) {
  const d = new Date();
  d.setDate(d.getDate() + daysAhead);
  d.setHours(9, 0, 0, 0);
  // Format as local datetime-local (yyyy-MM-ddTHH:mm)
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function ScheduleFollowupDialog({
  open,
  onOpenChange,
  customerEmail,
  customerName,
  invoices = [],         // optional [{id, invoice_number, grand_total, currency}]
  defaultInvoiceId,
  onCreated,
}) {
  const [title, setTitle] = useState('');
  const [dueIso, setDueIso] = useState(defaultDueIso(3));
  const [assigneeId, setAssigneeId] = useState('');
  const [invoiceId, setInvoiceId] = useState('');
  const [notes, setNotes] = useState('');
  const [users, setUsers] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setTitle(customerName ? `Call about ${customerName}` : 'AR follow-up');
      setDueIso(defaultDueIso(3));
      setAssigneeId('');  // server will default to caller
      setInvoiceId(defaultInvoiceId || '');
      setNotes('');
    }
  }, [open, customerName, defaultInvoiceId]);

  useEffect(() => {
    if (!open) return;
    listTenantUsers().then((r) => setUsers(r.data || [])).catch(() => setUsers([]));
  }, [open]);

  const selectedInvoice = useMemo(
    () => invoices.find((i) => i.id === invoiceId),
    [invoices, invoiceId],
  );

  const submit = async () => {
    if (title.trim().length < 2) {
      toast.error('Title is required');
      return;
    }
    setSubmitting(true);
    try {
      const r = await createArFollowup({
        title: title.trim(),
        due_date: new Date(dueIso).toISOString(),
        customer_email: customerEmail || undefined,
        customer_name: customerName || undefined,
        invoice_id: invoiceId || undefined,
        invoice_number: selectedInvoice?.invoice_number,
        notes: notes.trim() || undefined,
        assignee_user_id: assigneeId || undefined,
      });
      toast.success(`Follow-up scheduled · due ${new Date(r.data.due_date).toLocaleDateString()}`);
      onCreated?.(r.data);
      onOpenChange?.(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create follow-up');
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="schedule-followup-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ListChecks size={18} weight="bold" className="text-violet-600" />
            Schedule AR follow-up
          </DialogTitle>
          <DialogDescription>
            Track a call/email reminder for {customerName || customerEmail || 'this customer'} so it doesn't fall through the cracks.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="followup-title" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="flex items-center gap-1"><CalendarBlank size={12} /> Due</Label>
              <Input
                type="datetime-local"
                value={dueIso}
                onChange={(e) => setDueIso(e.target.value)}
                data-testid="followup-due"
              />
            </div>
            <div>
              <Label className="flex items-center gap-1"><User size={12} /> Assignee</Label>
              <select
                value={assigneeId}
                onChange={(e) => setAssigneeId(e.target.value)}
                className="w-full h-10 px-2 text-sm border border-zinc-200 bg-white rounded-md"
                data-testid="followup-assignee"
              >
                <option value="">Me (default)</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name || u.email} · {u.role}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {invoices.length > 0 && (
            <div>
              <Label>Linked invoice (optional)</Label>
              <select
                value={invoiceId}
                onChange={(e) => setInvoiceId(e.target.value)}
                className="w-full h-10 px-2 text-sm border border-zinc-200 bg-white rounded-md"
                data-testid="followup-invoice"
              >
                <option value="">— none —</option>
                {invoices.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.invoice_number} · {i.currency} {i.grand_total}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div>
            <Label>Notes</Label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="What to say…" data-testid="followup-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange?.(false)} data-testid="followup-cancel">Cancel</Button>
          <Button
            onClick={submit}
            disabled={submitting}
            className="bg-violet-600 hover:bg-violet-700"
            data-testid="followup-submit"
          >
            {submitting ? 'Saving…' : 'Schedule'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
